import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, TrendingUp, Target, Wrench, Clock, ChevronsUpDown, Lock, Receipt, Timer, Users } from "lucide-react";
import { toast } from "sonner";

// Local YYYY-MM-DD helper (avoids UTC surprises)
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

function presetRange(key) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  if (key === "today") {
    // start == end == today
  } else if (key === "week") {
    // Monday of this week
    const day = start.getDay(); // 0 Sun … 6 Sat
    const diff = (day + 6) % 7; // days since Monday
    start.setDate(start.getDate() - diff);
  } else if (key === "month") {
    start.setDate(1);
  }
  return { start: ymd(start), end: ymd(end) };
}

function money(v) {
  if (v === null || v === undefined) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(1)}%`;
}

export default function Analytics() {
  const { user } = useAuth();
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(() => presetRange("month"));
  const [kpis, setKpis] = useState(null);
  const [techs, setTechs] = useState([]);
  const [breakdown, setBreakdown] = useState({ by_ad_source: [], by_appliance_type: [] });
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("revenue"); // "revenue" | "jobs" | "ftc"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, t, b] = await Promise.all([
        api.get(`/analytics/kpis?start=${range.start}&end=${range.end}`),
        api.get(`/analytics/techs?start=${range.start}&end=${range.end}`),
        api.get(`/analytics/breakdown?start=${range.start}&end=${range.end}`),
      ]);
      setKpis(k.data);
      setTechs(t.data.rows || []);
      setBreakdown(b.data || { by_ad_source: [], by_appliance_type: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  const sortedTechs = useMemo(() => {
    const rows = [...techs];
    rows.sort((a, b) => {
      if (sortBy === "revenue") return (b.revenue || 0) - (a.revenue || 0);
      if (sortBy === "jobs") return (b.jobs || 0) - (a.jobs || 0);
      if (sortBy === "ftc") return (b.ftc_rate || 0) - (a.ftc_rate || 0);
      return 0;
    });
    return rows;
  }, [techs, sortBy]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-3" data-testid="analytics-gated">
        <Lock className="w-10 h-10 text-slate-400 mx-auto" />
        <h2 className="font-heading text-2xl font-bold text-slate-900">Admin only</h2>
        <p className="text-slate-600">Analytics show revenue and business KPIs. Ask an admin to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Analytics</h1>
          <p className="text-slate-500 text-sm">Revenue, jobs, first-time complete, and tech performance.</p>
        </div>
      </div>

      {/* Period picker */}
      <Card className="border-slate-200">
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                type="button"
                variant={preset === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => applyPreset(p.key)}
                className={preset === p.key ? "bg-blue-700 hover:bg-blue-800" : ""}
                data-testid={`preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="range-start" className="text-xs uppercase tracking-wider text-slate-500">Start</Label>
              <Input
                id="range-start"
                type="date"
                value={range.start}
                onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, start: e.target.value })); }}
                className="w-[160px]"
                data-testid="range-start"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="range-end" className="text-xs uppercase tracking-wider text-slate-500">End</Label>
              <Input
                id="range-end"
                type="date"
                value={range.end}
                onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, end: e.target.value })); }}
                className="w-[160px]"
                data-testid="range-end"
              />
            </div>
          </div>
          {loading && <span className="text-sm text-slate-500">Loading…</span>}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Kpi
          icon={<DollarSign className="w-5 h-5 text-emerald-700" />}
          label="Revenue"
          value={money(kpis?.revenue)}
          tone="emerald"
          testid="kpi-revenue"
        />
        <Kpi
          icon={<Wrench className="w-5 h-5 text-blue-700" />}
          label="Jobs completed"
          value={kpis?.jobs_completed ?? "—"}
          tone="blue"
          testid="kpi-jobs"
        />
        <Kpi
          icon={<Receipt className="w-5 h-5 text-emerald-700" />}
          label="Avg ticket value"
          value={money(kpis?.avg_ticket_value)}
          tone="emerald"
          testid="kpi-avg-ticket"
        />
        <Kpi
          icon={<Timer className="w-5 h-5 text-blue-700" />}
          label="Effective hourly rate"
          value={kpis?.effective_hourly_rate != null ? `${money(kpis.effective_hourly_rate)}/hr` : "—"}
          hint={kpis?.avg_hours != null ? `avg ${kpis.avg_hours}h/job` : ""}
          tone="blue"
          testid="kpi-hourly"
        />
        <Kpi
          icon={<Target className="w-5 h-5 text-amber-700" />}
          label="First-time complete"
          value={pct(kpis?.ftc_rate)}
          hint={kpis?.ftc_sample_size ? `${kpis.ftc_sample_size} rated` : "no data yet"}
          tone="amber"
          testid="kpi-ftc"
        />
        <Kpi
          icon={<Users className="w-5 h-5 text-amber-700" />}
          label="Repeat customer rate"
          value={pct(kpis?.repeat_customer_rate)}
          hint="past 12 months"
          tone="amber"
          testid="kpi-repeat"
        />
        <Kpi
          icon={<TrendingUp className="w-5 h-5 text-slate-700" />}
          label="Parts cost"
          value={money(kpis?.parts_cost)}
          tone="slate"
          testid="kpi-parts"
        />
        <Kpi
          icon={<DollarSign className="w-5 h-5 text-emerald-700" />}
          label="Net margin"
          value={money(kpis?.net_margin)}
          tone="emerald"
          testid="kpi-margin"
        />
      </div>

      {/* Tech leaderboard */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Tech leaderboard</h2>
            <span className="text-xs text-slate-500">Click a column header to sort</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Tech</TableHead>
                <SortableHeader label="Jobs" active={sortBy === "jobs"} onClick={() => setSortBy("jobs")} testid="sort-jobs" />
                <SortableHeader label="Revenue" active={sortBy === "revenue"} onClick={() => setSortBy("revenue")} testid="sort-revenue" />
                <SortableHeader label="FTC rate" active={sortBy === "ftc"} onClick={() => setSortBy("ftc")} testid="sort-ftc" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTechs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-slate-500 py-8" data-testid="techs-empty">
                    No completed jobs in this period yet.
                  </TableCell>
                </TableRow>
              )}
              {sortedTechs.map((r) => (
                <TableRow key={r.tech_id} data-testid={`tech-row-${r.tech_id}`}>
                  <TableCell className="font-medium">{r.tech_name}</TableCell>
                  <TableCell>{r.jobs}</TableCell>
                  <TableCell className="tabular-nums">{money(r.revenue)}</TableCell>
                  <TableCell>{pct(r.ftc_rate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Breakdown tables — where the money comes from */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownCard
          title="Revenue by ad source"
          hint="Where should you spend marketing next month?"
          rows={breakdown.by_ad_source}
          totalRevenue={kpis?.revenue}
          labelHead="Source"
          testid="breakdown-ad-source"
          formatKey={(k) => k.replace(/_/g, " ")}
        />
        <BreakdownCard
          title="Revenue by appliance"
          hint="Which appliances make you the most money?"
          rows={breakdown.by_appliance_type}
          totalRevenue={kpis?.revenue}
          labelHead="Appliance"
          testid="breakdown-appliance"
          formatKey={(k) => k.replace(/_/g, " ")}
        />
      </div>
    </div>
  );
}

function BreakdownCard({ title, hint, rows, totalRevenue, labelHead, testid, formatKey }) {
  const total = totalRevenue || 0;
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">{hint}</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">{labelHead}</TableHead>
              <TableHead>Jobs</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead className="w-[120px]">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!rows || rows.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                  No data yet for this period.
                </TableCell>
              </TableRow>
            )}
            {(rows || []).map((r) => {
              const share = total ? (r.revenue / total) * 100 : 0;
              return (
                <TableRow key={r.key}>
                  <TableCell className="font-medium capitalize">{formatKey(r.key)}</TableCell>
                  <TableCell>{r.jobs}</TableCell>
                  <TableCell className="tabular-nums">${r.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-full bg-slate-100 rounded h-2 overflow-hidden">
                        <div
                          className="h-2 bg-blue-600"
                          style={{ width: `${Math.min(100, share).toFixed(1)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-600 tabular-nums w-10 text-right">{share.toFixed(0)}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function Kpi({ icon, label, value, hint, tone = "slate", testid }) {
  const bg = {
    emerald: "bg-emerald-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    slate: "bg-slate-50",
  }[tone];
  return (
    <Card className={`border-slate-200`} data-testid={testid}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center gap-2 text-slate-600 text-sm">
          <span className={`w-8 h-8 rounded-md flex items-center justify-center ${bg}`}>{icon}</span>
          <span>{label}</span>
        </div>
        <div className="mt-3 font-heading text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
        {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

function SortableHeader({ label, active, onClick, testid }) {
  return (
    <TableHead>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900 font-semibold" : "text-slate-600"}`}
        data-testid={testid}
      >
        {label} <ChevronsUpDown className="w-3.5 h-3.5" />
      </button>
    </TableHead>
  );
}
